import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import DescriptionModeSelect from '@cohort/merchants/apps/common/DescriptionModeSelect';
import StepContentInput from '@cohort/merchants/apps/common/StepContentInput';
import StepTitleInput from '@cohort/merchants/apps/common/StepTitleInput';
import type {Defaults} from '@cohort/merchants/apps/common/triggers/utils';
import {defaultMedia, parseDefaults} from '@cohort/merchants/apps/common/triggers/utils';
import {getDescriptionMode} from '@cohort/merchants/apps/common/triggers/utils';
import ConnectionPicker from '@cohort/merchants/components/connections/ConnectionPicker';
import LanguageSelectorInput from '@cohort/merchants/components/form/LanguageSelectorInput';
import Separator from '@cohort/merchants/components/Separator';
import {useConnectionProfile} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import type {YoutubeMerchantConnectorStruct} from '@cohort/shared/apps/youtube/merchantConnector';
import type {YoutubeSubscribeTriggerStruct} from '@cohort/shared/apps/youtube/triggers/subscribe';
import type {Language} from '@cohort/shared/schema/common';
import {YOUTUBE_URL} from '@cohort/shared-frontend/common/apps/youtube';
import {Fragment, useEffect, useRef} from 'react';

const getDefaults = (definedLanguages: Language[]): Defaults => {
  const defaults = {
    title: {
      en: `Subscribe to our Youtube channel`,
      fr: `Abonnez-vous à notre chaîne Youtube`,
      es: `Suscríbete a nuestro canal de Youtube`,
      de: `Abonnieren Sie unseren YouTube-Kanal`,
    },
    description: {},
  };
  return parseDefaults(defaults, definedLanguages);
};

const YoutubeSubscribeTriggerConfigComponent: React.FC<
  TriggerIntegrationConfigComponentProps<YoutubeSubscribeTriggerStruct>
> = ({formReturn: {setValue, control, watch, reset, register}, trigger, step}) => {
  const init = useRef(false);
  const merchant = useCurrentMerchant();

  const [definedLanguages, connectionId] = watch(['definedLanguages', 'trigger.connectionId']);

  const {data: profile} = useConnectionProfile<YoutubeMerchantConnectorStruct>(
    merchant.id,
    connectionId ?? '',
    {
      enabled: !!connectionId && init.current,
    }
  );

  useEffect(() => {
    if (profile) {
      const defaults = getDefaults(definedLanguages);
      setValue('defaultTitle', defaults.title);
      setValue('defaultDescription', defaults.description);
      setValue(
        'trigger.triggerIntegrationConfig.channelUrl',
        `${YOUTUBE_URL}/${profile.properties.channelHandle ?? profile.properties.channelTitle}`
      );
    }
  }, [definedLanguages, profile, setValue]);

  useEffect(() => {
    if (init.current) {
      return;
    }
    const defaults = getDefaults(definedLanguages);
    reset(formValues => ({
      ...formValues,
      defaultTitle: defaults.title,
      defaultDescription: defaults.description,
      descriptionMode: getDescriptionMode(definedLanguages, defaults, step),
      title: step?.title ?? defaults.title,
      description: step?.description ?? defaults.description,
      icon: step?.icon !== undefined ? step.icon : '🫱🏼‍🫲🏿',
      media: step?.media ?? defaultMedia,
      trigger: {
        id: trigger?.id ?? null,
        connectionId: trigger?.connectionId ?? null,
        triggerIntegrationId: 'youtube.subscribe',
        triggerIntegrationConfig: {
          channelUrl: trigger?.triggerIntegrationConfig.channelUrl ?? '',
        },
      },
      statementEnabled: false,
    }));
    init.current = true;
  }, [definedLanguages, reset, step, trigger]);

  if (!init.current) {
    return null;
  }

  return (
    <Fragment>
      <div className="max-w-sm">
        <ConnectionPicker
          appId="youtube"
          control={control}
          register={register}
          connectionIdFieldName="trigger.connectionId"
        />
      </div>
      <DescriptionModeSelect />
      <LanguageSelectorInput />
      <StepTitleInput />
      <Separator />
      <StepContentInput />
    </Fragment>
  );
};

export default YoutubeSubscribeTriggerConfigComponent;
